import { type TaskView } from '@apm/contracts';
import { addDays, formatDate, parseDate } from '../gantt/scale';

/** Số ngày mặc định cho một việc chưa có ước lượng nào — APM-BOARD-c. */
const DEFAULT_SPAN_DAYS = 3;

/** Giờ làm một ngày. Cùng con số với phần còn lại của hệ thống. */
const HOURS_PER_DAY = 8;

export interface ScheduleDrop {
  readonly startDate: string;
  readonly dueDate: string;
}

/**
 * Thả một việc lên Gantt thành **ngày bắt đầu và hạn** — APM-BOARD-c.
 *
 * ## Vì sao độ dài suy từ ước lượng, không phải một hằng số
 *
 * Người dùng thả để nói "làm việc này từ khoảng đây", không phải để nói "việc
 * này dài ba ngày". Lấy `estimateHours` là dùng con số họ **đã** nhập; bịa một
 * độ dài cố định cho mọi việc thì thanh 40 giờ và thanh 2 giờ dài bằng nhau, và
 * biểu đồ nói sai ngay từ lúc vẽ.
 *
 * ## ⚠️ Khoảng đóng: một việc một ngày kết thúc **cùng ngày** nó bắt đầu
 *
 * `dueDate` là ngày cuối *còn làm*, không phải mốc sau khi xong. Cộng thẳng số
 * ngày vào ngày bắt đầu sẽ cho mọi thanh dài thêm một ngày, và sai lệch ấy dồn
 * lại thành cả tuần trên một kế hoạch dài.
 *
 * Nhận **ngày**, không nhận toạ độ: trục thời gian là chuyện của Gantt, và kéo
 * nó ra tới đây nghĩa là mỗi chỗ muốn đặt lịch đều phải dựng lại một cái trục.
 */
export function scheduleFromDrop(task: TaskView, startDate: string): ScheduleDrop {
  const start = parseDate(startDate);

  const days =
    task.estimateHours === null
      ? DEFAULT_SPAN_DAYS
      : Math.max(1, Math.ceil(task.estimateHours / HOURS_PER_DAY));

  return { startDate: formatDate(start), dueDate: formatDate(addDays(start, days - 1)) };
}

/**
 * Thả vào sprint **đang chạy** là scope creep — nói ra lúc kéo.
 *
 * Đội đã chốt cam kết lúc bấm "bắt đầu sprint" (`APM-AGILE-a`), nên mỗi việc
 * thêm vào sau đó làm phạm vi tăng mà cam kết đứng yên. Đó chính là thứ chỉ số
 * "nhận bao nhiêu ÷ làm được bao nhiêu" sinh ra để phát hiện — và người dùng
 * không nên phát hiện ra nó ở burndown tuần sau.
 */
export function isScopeCreep(sprintStatus: string | undefined): boolean {
  return sprintStatus === 'active';
}
